import React from 'react';
import { Chip, Tooltip, Box, Typography, alpha, useTheme } from '@mui/material';
import { format } from 'date-fns';
import { Expense, ExpenseStatus } from '../../types';

interface Props {
  expense: Expense;
  /** Default 'small'. */
  size?: 'small' | 'medium';
}

/**
 * The canonical status indicator for an expense row. Centralises:
 *   - the colour palette (warning / success / error / neutral-grey)
 *   - the "reversed" derived state (status=APPROVED + isActive=false)
 *   - the dot-indicator visual rhythm used across the expense screens
 *
 * Reversed rows get a grey chip with a hover tooltip surfacing the audit
 * trail (who reversed, when, why) so a reader never has to open the detail
 * dialog to understand what happened.
 */
const ExpenseStatusChip: React.FC<Props> = ({ expense, size = 'small' }) => {
  const theme = useTheme();

  const isReversed =
    expense.status === ExpenseStatus.APPROVED && expense.isActive === false;

  const label = isReversed
    ? 'Reversed'
    : expense.status === ExpenseStatus.PENDING
      ? 'Pending'
      : expense.status === ExpenseStatus.APPROVED
        ? 'Approved'
        : 'Rejected';

  const color = isReversed
    ? theme.palette.text.secondary
    : expense.status === ExpenseStatus.PENDING
      ? theme.palette.warning.main
      : expense.status === ExpenseStatus.APPROVED
        ? theme.palette.success.main
        : theme.palette.error.main;

  const chip = (
    <Chip
      size={size}
      label={
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            component="span"
            sx={{
              width: 6, height: 6, borderRadius: '50%',
              bgcolor: color, flexShrink: 0,
            }}
          />
          <Box component="span" sx={{ letterSpacing: 0.3 }}>{label}</Box>
        </Box>
      }
      sx={{
        bgcolor: alpha(color, 0.08),
        color: color,
        fontWeight: 600,
        fontSize: size === 'small' ? '0.7rem' : '0.75rem',
        border: `1px solid ${alpha(color, 0.2)}`,
        textTransform: 'uppercase',
        height: size === 'small' ? 22 : 26,
        '& .MuiChip-label': { px: 1 },
        cursor: isReversed ? 'help' : 'default',
      }}
    />
  );

  if (!isReversed) return chip;

  const who = expense.reversedBy?.name || 'Unknown';
  const when = expense.reversedAt ? format(new Date(expense.reversedAt), 'MMM dd, yyyy') : '';
  const reason = expense.reversalReason || 'No reason recorded';
  return (
    <Tooltip
      title={
        <Box sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
          <Typography variant="caption" display="block"><strong>Reversed by:</strong> {who}</Typography>
          {when && (
            <Typography variant="caption" display="block"><strong>When:</strong> {when}</Typography>
          )}
          <Typography variant="caption" display="block"><strong>Reason:</strong> {reason}</Typography>
        </Box>
      }
      arrow
    >
      {chip}
    </Tooltip>
  );
};

export default ExpenseStatusChip;
